import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import { Box, Container, Typography, Grid, Card, Accordion, AccordionSummary, AccordionDetails, Breadcrumbs, Link, Chip, Paper, Avatar, Divider, List, ListItem, ListItemText, ListItemIcon, Button} from '@mui/material';
import { ExpandMore, Home, NavigateNext, School, VolumeUp, Psychology, Language,QuestionAnswer,TrendingUp,Star,Download,CheckCircle,UpdateRounded,MenuBook,BusinessCenter,RecordVoiceOver,Edit,LibraryBooks,MicExternalOn,ChildCare} from '@mui/icons-material';
import tips1Image from '../assets/images/tips1.webp';
import tips2Image from '../assets/images/tips2.webp';

const essentialVocabulary = {
  numbers: {
    title: 'Numbers (数字)',
    icon: '123',
    words: ['一 (yī)', '二 (èr)', '三 (sān)', '四 (sì)', '五 (wǔ)', '六 (liù)', '七 (qī)', '八 (bā)', '九 (jiǔ)', '十 (shí)', '百 (bǎi)', '千 (qiān)']
  },
  basicNouns: {
    title: 'Basic Nouns (基本名词代词)',
    icon: '🎯',
    words: ['人 (rén - person)', '口 (kǒu - mouth)', '日 (rì - sun/day)', '月 (yuè - moon/month)', '山 (shān - mountain)', '水 (shuǐ - water)', '我 (wǒ - I/me)', '你 (nǐ - you)', '他 (tā - he/him)', '她 (tā - she/her)']
  },
  basicVerbs: {
    title: 'Basic Verbs (基本动词形容词)',
    icon: '🏃',
    words: ['是 (shì - is/am/are)', '有 (yǒu - to have)', '大 (dà - big)', '小 (xiǎo - small)', '上 (shàng - up/on)', '下 (xià - down/under)', '做 (zuò - to do)', '来 (lái - to come)', '去 (qù - to go)', '说 (shuō - to say, to speak)', '看 (kàn - to look, to watch)', '听 (tīng - to listen)']
  },
  restaurant: {
    title: 'Restaurant (餐厅/点餐)',
    icon: '🍽️',
    words: [
      '菜单 (càidān - menu)',
      '筷子 (kuàizi - chopsticks)',
      '勺子 (sháozi - spoon)',
      '碗 (wǎn - bowl)',
      '盘子 (pánzi - plate)',
      '杯子 (bēizi - cup/glass)',
      '水 (shuǐ - water)',
      '茶 (chá - tea)',
      '啤酒 (píjiǔ - beer)',
      '饿 (è - hungry)',
      '饱 (bǎo - full)',
      '好吃 (hǎochī - delicious)',
      '买单/结账 (mǎidān/jiézhàng - bill)'
    ]
  },
  shopping: {
    title: 'Shopping (购物/砍价)',
    icon: '🛍️',
    words: [
      '多少钱？(Duōshǎo qián? - How much?)',
      '便宜 (piányi - cheap)',
      '贵 (guì - expensive)',
      '买 (mǎi - to buy)',
      '卖 (mài - to sell)',
      '衣服 (yīfu - clothes)',
      '鞋子 (xiézi - shoes)',
      '大小 (dàxiǎo - size)',
      '试穿 (shìchuān - to try on)',
      '试衣间 (shìyījiān - fitting room)',
      '现金 (xiànjīn - cash)',
      '手机支付 (shǒujī zhīfù - mobile payment)'
    ]
  },
  accommodation: {
    title: 'Accommodation / Hotel (住宿/酒店)',
    icon: '🏨',
    words: [
      '酒店 (jiǔdiàn - hotel)',
      '房间 (fángjiān - room)',
      '预订 (yùdìng - reservation)',
      '入住 (rùzhù - check-in)',
      '退房 (tuìfáng - check-out)',
      '钥匙 (yàoshi - key)',
      '房卡 (fángkǎ - key card)',
      'Wi-Fi (wǎifài - Wi-Fi)',
      '空调 (kōngtiáo - air conditioner)',
      '早餐 (zǎocān - breakfast)'
    ]
  }
};

const knowledgeData = {
  title: 'Chinese Learning Tips',
  description: 'Discover expert insights, proven strategies, and practical advice to accelerate your Chinese learning journey. Updated regularly with new tips and techniques.',
  
  categories: [
    {
      id: 'pronunciation',
      title: 'Pronunciation & Pinyin',
      icon: <VolumeUp />,
      color: '#3b82f6'
    },
    {
      id: 'characters',
      title: 'Chinese Characters',
      icon: <Language />,
      color: '#10b981'
    },
    {
      id: 'tones',
      title: 'Tones & Phonetics',
      icon: <Psychology />,
      color: '#f59e0b'
    },
    {
      id: 'learning-methods',
      title: 'Learning Methods',
      icon: <School />,
      color: '#8b5cf6'
    }
  ],

  faqs: [
    {
      id: 1,
      question: 'Should I Learn Pinyin or Spoken Chinese First?',
      category: 'pronunciation',
      answer: `I strongly recommend <b>starting with Pinyin and pronunciation</b> to build a solid foundation, then moving quickly into listening and speaking practice so that your spoken Chinese sounds natural from the beginning.

Think of Pinyin as the <b>roadmap for Chinese pronunciation</b> — and your early "training wheels." Learning it well helps you speak faster, more accurately, and with confidence. It's like learning the pitch of each musical note before you start singing — once you have it, you won't sing off-key.

If pronunciation habits form without proper correction through Pinyin, they can be very hard to change later.

The best approach is not to spend months on Pinyin in isolation, but to <b>learn it systematically and immediately pair it with speaking practice</b>. Investing a short amount of time in getting Pinyin right is like sharpening your tools before working — it makes everything that follows easier and more effective, and pays off in the long run.`,
      tags: ['pinyin', 'pronunciation', 'beginner', 'foundation']
    },
    {
      id: 2,
      question: 'Chinese tones seem so difficult — how can I master them?',
      category: 'tones',
      answer: `Use a combination of the <b>"hand gesture method"</b> (see below) and the <b>"five-scale tone marking system"</b> (see below) to clearly visualize pitch movement. Then, follow a step-by-step, structured practice routine. With consistent, systematic training, mastering tones becomes not just possible — but surprisingly manageable.

Begin by distinguishing the four fundamental tonal contours and their pitch variations.Practice with full words and phrases, not just isolated syllables.`,
      answerPart2: `Chinese is often described as a "<b>melodic language</b>" or "<b>tonal language</b>." Unlike English, where pitch mainly changes at the level of an entire sentence — rising for questions or falling for statements — Mandarin assigns a fixed pitch pattern, or tone, to <b>every single syllable</b>.

This concept has no exact equivalent in most other languages, which means learners' brains and ears are not naturally trained to process pitch changes that <b>distinguish word meaning</b>. For example:

• <b>mā</b> (妈 - mother) vs. <b>má</b> (麻 - hemp) vs. <b>mǎ</b> (马 - horse) vs. <b>mà</b> (骂 - to scold)
• <b>yóuyǒng</b> (游泳 - to swim) vs. <b>yǒuyòng</b> (有用 - useful)
• <b>tōngzhī</b> (通知 - to notify) vs. <b>tǒngzhì</b> (统治 - to rule)

<b>The Core Secret</b>: Listen more, imitate often, speak bravely, and don't be afraid of making mistakes.

<b>How to Do It</b>: Shift your mindset, train your ears, embrace imperfection, and turn tone practice into a daily habit.

<b>Tip for the Neutral Tone: There are clever techniques to master it too! (contact us)</b>

•	<b>东西</b> dōngxī (east and west) vs. <b>东西</b> dōngxi (thing)
•	<b>孙子</b> sūnzǐ (Sun Tzu, the military strategist) vs. <b>孙子</b> sūnzi (grandson)

Tones are the melody of Mandarin. Once you get the hang of them, speaking Chinese starts to feel almost like singing — full of rhythm and flow, and surprisingly fun. Stay consistent and keep practicing, and you'll conquer this challenge with confidence. Keep going — you’ve got this!`,
      tags: ['tones', 'pronunciation', 'phonetics', 'difficulty'],
      hasImages: true
    },
    {
      id: 3,
      question: 'Can I Learn Only Pinyin Without Learning Chinese Characters?',
      category: 'characters',
      answer: `<b>Technically, yes</b>. Focusing only on Pinyin can help you start speaking quickly, lower the learning barrier, and is <b>suitable for</b> short-term travellers, casual daily conversations with close friends, or early language exposure for heritage children.

However, it comes with <b>significant limitations</b> — it will not enable you to truly learn Chinese!

<b>The Core Challenge — Massive Homophones:</b>
Mandarin has over 400 unique syllables. Even when combined with the four tones, there are only about 1,300 tone-syllable combinations — but they must represent thousands of characters.

For example, the Pinyin syllable "<b>shì</b>" can correspond to dozens of different characters: <b>是 (to be), 事 (matter), 市 (market), 试 (to try), 室 (room), 视 (to look at), 世 (world), 式 (style), 示 (to show), 势 (force), 适 (suitable), 释 (to explain)</b>, and many more. Without learning characters, you cannot reliably tell them apart.

<b>Inability to Read Independently:</b>
Road signs, menus, news articles, books, social media posts, product instructions — the real Chinese world is almost entirely written in characters, and Pinyin-only publications are extremely rare. Learners who skip characters are unable to engage in formal, written, or in-depth communication.

<b>Limited Spoken Proficiency:</b>
Without characters, every new word is just a string of letters and tones. It becomes much harder to memorize and use words accurately, leaving learners stuck at a "survival Chinese" level and unable to discuss complex or meaningful topics.

<b>No Access to Chinese Culture:</b>
Chinese characters are the carriers of Chinese culture. Each character has its own history and story. Idioms, poetry, calligraphy, and even many jokes are based on the structure or wordplay of characters. Without characters, you miss out on the deeper cultural layers of the language.`,
      tags: ['characters', 'pinyin', 'reading', 'culture']
    },
    {
      id: 4,
      question: 'Where should I start learning Chinese characters?',
      category: 'characters',
      answer: `Don't worry — while learning Chinese characters does require time and patience, it's absolutely achievable! That's because Chinese characters are built on recognizable patterns and a logical structure.

A great way to start is by focusing on:
• Frequently used characters
• Pictographic characters (象形字 xiàngxíngzì)
• Basic radicals (偏旁部首 piānpáng bùshǒu)

Understanding Common Radicals and Character Decomposition:
• "女" (nǚ, woman radical): Used in characters related to women and feminine concepts
  Examples: 妈 (mā, mother) = 女 (woman) + 马 (mǎ, horse for pronunciation)
• "口" (kǒu, mouth radical): Used in characters related to mouth, speaking, eating
  Examples: 吃、喝、叫、唱、哭 (eating, drinking, calling, singing, crying)
• "扌" (hand radical): Used in characters related to hand actions
  Examples: 打、拉、推、扔、找 (hitting, pulling, pushing, throwing, searching)
• "氵" (water radical): Used in characters related to water and liquids
  Examples: 江、河、湖、海、清 (river, stream, lake, sea, clear)

Most importantly, <b>phonetic-semantic compounds (形声字 xíngshēngzì)</b> make up over 80% of all Chinese characters. Understanding how these characters work — where one part suggests meaning and the other hints at pronunciation — is the key to learning efficiently and effectively.

Our Chinese character teaching method is designed to be both efficient and engaging — you'll be reading and recognizing characters faster than you think!

•	<b>Common radicals + Decomposition mnemonics</b>
“女”（女字旁），形旁+声旁，如 “妈 = 女 + 马”）、“口”（口字旁）吃、喝、叫、唱、哭……“扌”（提手旁）打、拉、推、扔、找……“氵”（三点水）江、河、湖、海、清……

•	Focus on <b>character recognition and typing</b> first; practice handwriting gradually only if there is interest or specific need.

•	Learn through <b>thematic scenarios and categorized vocabulary</b> to improve memorization efficiency.`,
      tags: ['characters', 'beginner', 'roadmap', 'learning-strategy'],
    },
    {
      id: 5,
      question: 'I have memorized a considerable number of vocabulary words, but I still struggle to use them effectively in context. What should I do?',
      category: 'learning-methods',
      answer: `Make vocabulary <b>come alive</b>: Shift from "learning words" → to "learning collocations" → to "learning sentences" → to "using them in real-life scenarios".

Use the "<b>Contextual Memorization Method</b>" to associate words with real situations, empowering you to actually use what you've learned. (see below)

•	<b>Memorize common word collocations to make their usage feel natural and automatic.</b>
👎 Wrong Example: 安排 (ānpái) - to arrange
👍 Correct Example:
•	<b>Memorize word chunks and collocations.</b> 
<span style="display: inline-block; text-align: center;"><span style="display: block; font-size: 0.8em; color: #666;">ān pái huì yì</span>安排会议</span> (arrange a meeting)
<span style="display: inline-block; text-align: center;"><span style="display: block; font-size: 0.8em; color: #666;">ān pái shí jiān</span>安排时间</span> (arrange time)

•	<b>Memorize and internalize full sentence patterns.</b> 
<span style="display: inline-block; text-align: center;"><span style="display: block; font-size: 0.8em; color: #666;">wǒ lái ān pái yí xià míng tiān de huì yì</span>我来安排一下明天的会议。</span> (I'll arrange tomorrow's meeting.)
<span style="display: inline-block; text-align: center;"><span style="display: block; font-size: 0.8em; color: #666;">nǐ shén me shí hou yǒu kòng？wǒ men ān pái gè shí jiān jiàn miàn</span>你什么时候有空？我们安排个时间见面。</span> (When are you free? Let's arrange a time to meet.)

• Learn words through sentence context and master sentence construction.

After learning a new word, immediately create 1–2 personalized sentences related to your own experience. The more personal the context, the deeper the retention.

For example: 羡慕 (xiànmù - to envy)
造句：①我很羡慕我的同学，因为他会说三种语言。②你不要羡慕我，我只是运气好。

Substitution drill: Use a fixed sentence pattern and replace key words within it.
For example:
句型：我用手机看电影。
替换：我用电脑工作。 / 我用筷子吃饭。 / 我用微信和朋友聊天。

<i>Remember</i>: Language is a skill, not just knowledge. Like learning to swim, no amount of reading alone will make you proficient without getting into the water. The vocabulary you've accumulated are your "swimming moves." Now it's time to dive into the "water"—engage in substantial output and practice—to learn how to coordinate them effectively. Persist, and you will soon break through this plateau!`,
      tags: ['vocabulary', 'context', 'practical-usage', 'scenarios']
    },
    {
      id: 6,
      question: 'What should I do if I\'m not seeing significant progress in my Chinese learning?',
      category: 'learning-methods',
      answer: `This absolutely does not mean you lack ability——rather, it indicates that your learning methods or strategies require adjustment. The learning curve does not rise in a straight line but rather in steps. Every learner experiences a period of rapid ascent followed by a prolonged plateau phase. After accumulating knowledge to a certain level, they leap to the next stage. View the "plateau phase" as a signal that you are building momentum for your next leap. What you need to do is "identify the root cause" and "adjust your strategy."

🔍 <b>Step 1: Ask yourself these questions to locate the issue:</b>
• Are your goals clear?
❌ Vague goal: "I want to learn Chinese well."
✅ Clear goal: "Within three months, I will be able to use Chinese for daily tasks like ordering in restaurants and bargaining while shopping," or "I will master the 300 core vocabulary words for HSK Level 3."
• Are your methods inefficient?
•	Are you passively memorizing words or swiping through apps without actively forming sentences or producing language?
•	Are you learning scattered content without connecting it through context or sentences?
•	Are you focusing only on "learning" while neglecting "review"? Regular review is essential—the Ebbinghaus forgetting curve is real.
• Is there a balance between input and output?
Is your input (listening and reading) much greater than your output (speaking and writing)? This can lead to "dumb Chinese"—understanding text but being unable to understand speech or express yourself.
• Is the content relevant to real life?
Is what you're learning too formal, or not related to your interests? Being unable to use what you learn can quickly diminish motivation.

🛠️ <b>Step 2: Adjust your action plan</b>
• Set Clear and Achievable Goals
Take action now: forget the vague goal of "learning Chinese well." Set a very specific short-term goal for the next 2–4 weeks.
Example: "This week, I will master 10 sentences related to ordering food and use them fluently in role-playing."
Example: "Today, I will learn to describe my family and job in Chinese."
• Optimize Your Learning Methods
Move from recognition to application:
•	Force yourself to create sentences: Every time you learn a new word or grammar point, immediately use it in a personal sentence. The weirder or more personal, the better you'll remember.
•	Embrace scenario-based learning: Don't learn words in isolation. Learn a full scenario, like "ordering coffee in a café." You'll acquire a ready-to-use toolkit.
•	Build knowledge connections: When learning something new, actively relate it to what you already know. For example, when you learn 热情 (rèqíng, passionate), think about how it differs from 热心 (rèxīn, warm-hearted) or 热闹 (rènao, lively).
• Create an Immersive Environment
•	Change your device language to Chinese for direct immersion.
•	Background listening: Play Chinese podcasts, music, or TV shows while cooking or commuting—you don't need to understand everything; the goal is acclimation.
•	Find a study buddy or language partner:
o	Study buddy: A friend also learning Chinese—you can motivate each other and share insights.
o	Language partner: A native Chinese speaker. Even just 15 minutes of real conversation per week is far more effective than hours of solo study.
• Build Confidence
•	Reward yourself after achieving small goals.
•	Focus on what you've learned, not what you haven't. Reflect: "A month ago, I couldn't say this sentence—now I can!" This sense of progress is vital for motivation.
• Make It Fun Again!
If learning feels dull, change it up:
•	Try singing Chinese songs.
•	Watch videos of Chinese creators you enjoy (food, gaming, tech—anything!).
•	Look up Chinese puns or short jokes.

<i>Remember</i>: every moment of difficulty is a sign that your brain is growing and adapting. CHIHUI Mandarin Hub will be here with you all the way. Keep going—you've got this!`,
      tags: ['progress', 'plateau', 'learning-strategy', 'motivation']
    }
  ],

  tips: [
    {
      title: 'Start with High-Frequency Characters',
      description: 'Focus on the most commonly used characters first - they appear in 60% of daily texts.',
      icon: <TrendingUp />,
      category: 'characters'
    },
    {
      title: 'Use Visual Memory Techniques',
      description: 'Connect characters with images and stories to make them easier to remember.',
      icon: <Psychology />,
      category: 'learning-methods'
    },
    {
      title: 'Practice Tones with Hand Gestures&Five-Scale Tone Marking',
      description: 'Use physical movements to reinforce tone patterns and make them more memorable.',
      icon: <VolumeUp />,
      category: 'tones'
    },
    {
      title: 'Learn Radicals First',
      description: 'Understanding character components helps you decode new characters more easily.',
      icon: <Language />,
      category: 'characters'
    }
  ]
};

function Knowledge() {
  const [expandedFAQ, setExpandedFAQ] = useState('panel1');

  const handleFAQChange = (panel) => (event, isExpanded) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  const filteredFAQs = knowledgeData.faqs;

  return (
    <>
      <SEOHead pageKey="knowledge" />
      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative'
      }}>
      {/* Header Section */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 6, md: 12 },
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 75%, #475569 100%)',
          color: 'white',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(245, 158, 11, 0.12) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(168, 85, 247, 0.08) 0%, transparent 50%),
              linear-gradient(45deg, rgba(14, 165, 233, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%)
            `,
            pointerEvents: 'none'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(2px 2px at 20px 30px, rgba(255, 255, 255, 0.1), transparent),
              radial-gradient(2px 2px at 40px 70px, rgba(255, 255, 255, 0.05), transparent),
              radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.08), transparent),
              radial-gradient(1px 1px at 130px 80px, rgba(255, 255, 255, 0.06), transparent)
            `,
            backgroundRepeat: 'repeat',
            backgroundSize: '150px 100px',
            animation: 'float 20s ease-in-out infinite',
            pointerEvents: 'none',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-10px)' }
            }
          }
        }}
      >
        {/* Decorative geometric shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
            filter: 'blur(2px)',
            animation: 'pulse 4s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.3, transform: 'scale(1)' },
              '50%': { opacity: 0.6, transform: 'scale(1.1)' }
            }
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: '60%',
            right: '8%',
            width: '80px',
            height: '80px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(239, 68, 68, 0.1))',
            transform: 'rotate(45deg)',
            animation: 'rotate 8s linear infinite',
            '@keyframes rotate': {
              '0%': { transform: 'rotate(45deg)' },
              '100%': { transform: 'rotate(405deg)' }
            }
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            right: '15%',
            width: '60px',
            height: '60px',
            background: 'linear-gradient(45deg, rgba(16, 185, 129, 0.1), rgba(59, 130, 246, 0.1))',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            animation: 'float 6s ease-in-out infinite reverse',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>

            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                mb: 4,
                fontSize: { xs: '2.8rem', md: '4.2rem'},
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 50%, #f1f5f9 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '200px',
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                }
              }}
            >
              {knowledgeData.title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: { xs: 0, md: 8 },
                opacity: 0.85,
                maxWidth: 800,
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                lineHeight: 1.8,
                color: '#cbd5e1',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                fontWeight: 300,
                letterSpacing: '0.5px'
              }}
            >
              {knowledgeData.description}
            </Typography>

            {/* Call-to-action buttons */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              gap: 3, 
              justifyContent: 'center',
              flexWrap: 'wrap',
              mt: 6
            }}>
              <Box
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: '50px',
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(147, 51, 234, 0.2))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'default',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 30px rgba(59, 130, 246, 0.3)',
                  }
                }}
              >
                <Box
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#10b981',
                      animation: 'blink 2s infinite',
                      '@keyframes blink': {
                        '0%, 50%': { opacity: 1 },
                        '51%, 100%': { opacity: 0.3 }
                      }
                    }}
                  />
                  Updated Regularly
                </Box>
              </Box>

              <Box
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: '50px',
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(239, 68, 68, 0.2))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'default',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 30px rgba(245, 158, 11, 0.3)',
                  }
                }}
              >
                <Typography
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  ⭐ Expert Insights
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>

        {/* Bottom wave decoration */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(180deg, transparent 0%, rgba(248, 250, 252, 0.1) 100%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #f59e0b, #10b981)',
            }
          }}
        />
      </Box>

      {/* Breadcrumb Navigation */}
      <Box sx={{ 
        bgcolor: 'white', 
        py: 2, 
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <Container maxWidth="xl">
          <Breadcrumbs 
            separator={<NavigateNext fontSize="small" />} 
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              href="/"
            >
              <Home sx={{ mr: 0.5 }} fontSize="inherit" />
              Home
            </Link>
            <Typography>
              Knowledge Base
            </Typography>
          </Breadcrumbs>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>

        {/* FAQ Section */}
        <Box sx={{ mb: 12 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'serif',
                fontWeight: 600,
                color: '#1e293b',
                mb: 2
              }}
            >
              Frequently Questions
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: 600,
                mx: 'auto',
                fontSize: '1.1rem'
              }}
            >
              Expert answers to common Chinese learning questions
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              <Box sx={{ mb: 4 }}>
                {filteredFAQs.map((faq) => (
                  <Accordion
                    key={faq.id}
                    expanded={expandedFAQ === `panel${faq.id}`}
                    onChange={handleFAQChange(`panel${faq.id}`)}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        py: 2,
                        px: 3,
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: 'rgba(59, 130, 246, 0.1)',
                            color: '#3b82f6',
                            mr: 2,
                            display: { xs: 'none', md: 'flex' }
                          }}
                        >
                          <QuestionAnswer fontSize="small" />
                        </Avatar>
                        <Typography
                          variant="h6"
                          sx={{
                            color: '#1e293b',
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            flex: 1
                          }}
                        >
                          Q{faq.id}: {faq.question}
                        </Typography>
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, ml: 2 }}>
                          {faq.tags.slice(0, 2).map((tag, index) => {
                            // 定义四种颜色配置
                            const colorConfigs = [
                              { bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }, // 蓝色
                              { bgcolor: 'rgba(251, 191, 36, 0.1)', color: '#f59e0b' }, // 黄色
                              { bgcolor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }, // 绿色
                              { bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }  // 红色
                            ];
                            
                            // 基于标签内容和索引生成随机颜色
                            const colorIndex = (tag.charCodeAt(0) + index) % 4;
                            const selectedColor = colorConfigs[colorIndex];
                            
                            return (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                sx={{
                                  bgcolor: selectedColor.bgcolor,
                                  color: selectedColor.color,
                                  fontSize: '0.75rem',
                                  height: 24
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: '#9f2122',
                            color: 'white',
                            mr: 3,
                            mt: 0.5,
                            flexShrink: 0,
                            display: { xs: 'none', md: 'flex' }
                          }}
                        >
                          <Star fontSize="small" />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#9f2122',
                              fontWeight: 600,
                              mb: 1,
                              fontSize: '1rem'
                            }}
                          >
                            CHIHUI:
                          </Typography>
                          {/* Q2 with images */}
                          {faq.id === 2 ? (
                            <>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: '#374151',
                                  lineHeight: 1.7,
                                  fontSize: '1rem',
                                  whiteSpace: 'pre-line'
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: faq.answer
                                }}
                              />
                              
                              {/* Images for Q2 */}
                              <Box sx={{ mt: 4 }}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                      <img 
                                        src={tips1Image} 
                                        alt="Hand Gesture Method"
                                        style={{
                                          width: '100%',
                                          maxWidth: '400px',
                                          height: 'auto',
                                          borderRadius: '12px',
                                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          mt: 2,
                                          color: '#64748b',
                                          fontWeight: 500,
                                          fontSize: '0.95rem'
                                        }}
                                      >
                                        Hand Gesture Method
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                      <img 
                                        src={tips2Image} 
                                        alt="Five-Scale Tone Marking System"
                                        style={{
                                          width: '100%',
                                          maxWidth: '400px',
                                          height: 'auto',
                                          borderRadius: '12px',
                                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          mt: 2,
                                          color: '#64748b',
                                          fontWeight: 500,
                                          fontSize: '0.95rem'
                                        }}
                                      >
                                        Five-Scale Tone Marking System
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                              
                              <Typography
                                variant="body1"
                                sx={{
                                  color: '#374151',
                                  lineHeight: 1.7,
                                  fontSize: '1rem',
                                  whiteSpace: 'pre-line',
                                  mt: 4
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: faq.answerPart2
                                }}
                              />
                            </>
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                color: '#374151',
                                lineHeight: 1.7,
                                fontSize: '1rem',
                                whiteSpace: 'pre-line'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: faq.answer
                              }}
                            />
                          )}
                          
                          {/* Character List for Q4 */}
                          {faq.id === 4 && faq.characterList && (
                            <Box sx={{ mt: 4 }}>
                              
                              <Box sx={{ mt: 4, textAlign: 'center' }}>
                                <Button
                                  variant="contained"
                                  startIcon={<Download />}
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = '/现代汉语常用字表.pdf';
                                    link.download = '现代汉语常用字表.pdf';
                                    link.target = '_blank';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}
                                  sx={{
                                    bgcolor: '#9f2122',
                                    color: 'white',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': {
                                      bgcolor: '#dc2626'
                                    }
                                  }}
                                >
                                  Download Complete Character List
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ position: 'sticky', top: 100 }}>
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#1e293b',
                      fontWeight: 600,
                      mb: 3,
                      fontSize: '1.2rem'
                    }}
                  >
                    Learning Progress
                  </Typography>
                  
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        mb: 2,
                        fontSize: '0.9rem'
                      }}
                    >
                      Character Learning Roadmap
                    </Typography>
                    <Box sx={{ space: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CheckCircle sx={{ color: '#10b981', mr: 1, fontSize: '1.2rem' }} />
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.9rem' }}>
                          300 characters → 60% of daily texts
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CheckCircle sx={{ color: '#10b981', mr: 1, fontSize: '1.2rem' }} />
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.9rem' }}>
                          800 characters → 90% of daily content
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CheckCircle sx={{ color: '#10b981', mr: 1, fontSize: '1.2rem' }} />
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.9rem' }}>
                          1,500+ characters → News articles
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Typography
                    variant="h6"
                    sx={{
                      color: '#1e293b',
                      fontWeight: 600,
                      mb: 3,
                      fontSize: '1.1rem'
                    }}
                  >
                    Quick Resources
                  </Typography>
                  
                  <Box sx={{ space: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Download />}
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = '/现代汉语常用字表.pdf';
                        link.download = '现代汉语常用字表.pdf';
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      sx={{
                        mb: 2,
                        borderColor: '#07284b',
                        color: '#07284b',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#1e40af',
                          bgcolor: 'rgba(7, 40, 75, 0.05)'
                        }
                      }}
                    >
                      Character List PDF
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<VolumeUp />}
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = '/Pronunciation-Guide.pdf';
                        link.download = 'Pronunciation-Guide.pdf';
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      sx={{
                        mb: 2,
                        borderColor: '#9f2122',
                        color: '#9f2122',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#dc2626',
                          bgcolor: 'rgba(159, 33, 34, 0.05)'
                        }
                      }}
                    >
                      Pronunciation Guide
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<School />}
                      sx={{
                        borderColor: '#10b981',
                        color: '#10b981',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#059669',
                          bgcolor: 'rgba(16, 185, 129, 0.05)'
                        }
                      }}
                    >
                      Study Schedule Template
                    </Button>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* More Chinese Learning Resources */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <UpdateRounded sx={{ color: '#9f2122', mr: 1, fontSize: '1.4rem' }} />
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#1e293b',
                          fontWeight: 600,
                          fontSize: '1.1rem'
                        }}
                      >
                        More Chinese Learning Resources
                      </Typography>
                    </Box>

                    <Box sx={{ 
                      bgcolor: 'rgba(159, 33, 34, 0.05)', 
                      borderRadius: 3, 
                      p: 3, 
                      mb: 3,
                      border: '1px solid rgba(159, 33, 34, 0.1)'
                    }}>
                      <Box sx={{ mb: 2 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<Download />}
                          onClick={() => {
                            window.open('https://wa.me/6589208197', '_blank');
                          }}
                          sx={{
                            background: 'linear-gradient(135deg, rgba(159, 33, 34, 0.8) 0%, rgba(159, 33, 34, 0.9) 100%)',
                            color: 'white',
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            boxShadow: '0 4px 15px rgba(159, 33, 34, 0.25)',
                            border: '1px solid rgba(159, 33, 34, 0.2)',
                            position: 'relative',
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, rgba(159, 33, 34, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)',
                              boxShadow: '0 6px 20px rgba(159, 33, 34, 0.35)',
                              transform: 'translateY(-2px)',
                              transition: 'all 0.3s ease',
                              border: '1px solid rgba(159, 33, 34, 0.3)'
                            },
                            '&:active': {
                              transform: 'translateY(0px)',
                              boxShadow: '0 2px 10px rgba(159, 33, 34, 0.25)'
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                              transition: 'left 0.5s'
                            },
                            '&:hover::before': {
                              left: '100%'
                            }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            position: 'relative',
                            zIndex: 1
                          }}>
                            Free Resources
                          </Box>
                        </Button>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 1,
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            textAlign: 'center'
                          }}
                        >
                          Contact us to get free learning resources
                        </Typography>
                      </Box>
                      
                      <List dense sx={{ p: 0 }}>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <BusinessCenter sx={{ fontSize: '1rem', color: '#9f2122' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Business Chinese High-Frequency Vocabulary"
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.9rem',
                                color: '#374151',
                                fontWeight: 500
                              }
                            }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <RecordVoiceOver sx={{ fontSize: '1rem', color: '#9f2122' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="PSLE Oral Practice Collection (100 Articles)"
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.9rem',
                                color: '#374151',
                                fontWeight: 500
                              }
                            }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <MenuBook sx={{ fontSize: '1rem', color: '#9f2122' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="O Level Essential Idioms for Reading & Writing"
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.9rem',
                                color: '#374151',
                                fontWeight: 500
                              }
                            }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Edit sx={{ fontSize: '1rem', color: '#9f2122' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="IGCSE High-Scoring Writing Patterns & Vocabulary"
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.9rem',
                                color: '#374151',
                                fontWeight: 500
                              }
                            }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <LibraryBooks sx={{ fontSize: '1rem', color: '#9f2122' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="IB Chinese A Literary Analysis"
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.9rem',
                                color: '#374151',
                                fontWeight: 500
                              }
                            }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <VolumeUp sx={{ fontSize: '1rem', color: '#9f2122' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Mandarin Pronunciation Practice Workbook"
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.9rem',
                                color: '#374151',
                                fontWeight: 500
                              }
                            }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <ChildCare sx={{ fontSize: '1rem', color: '#9f2122' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Children's Eloquence Story Collection"
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.9rem',
                                color: '#374151',
                                fontWeight: 500
                              }
                            }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <MicExternalOn sx={{ fontSize: '1rem', color: '#9f2122' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="OPI Oral Interview Advanced Phrases"
                            primaryTypographyProps={{
                              sx: {
                                fontSize: '0.9rem',
                                color: '#374151',
                                fontWeight: 500
                              }
                            }}
                          />
                        </ListItem>
                      </List>
                    </Box>
                    
                    
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        mb: 3,
                        fontSize: '0.9rem',
                        fontStyle: 'italic'
                      }}
                    >
                      Continuously updating...
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Quick Tips Section */}
        <Box sx={{ mb: 12 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'serif',
              fontWeight: 600,
              color: '#1e293b',
              mb: 4,
              textAlign: 'center'
            }}
          >
            Quick Learning Tips
          </Typography>
          <Grid container spacing={4}>
            {knowledgeData.tips.map((tip, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: 'none',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)'
                    }
                  }}
                >
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'rgba(59, 130, 246, 0.1)',
                        color: '#3b82f6',
                        mb: 2,
                        mx: 'auto'
                      }}
                    >
                      {tip.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        mb: 2,
                        fontSize: '1.1rem'
                      }}
                    >
                      {tip.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        lineHeight: 1.5,
                        fontSize: '0.95rem'
                      }}
                    >
                      {tip.description}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Essential Vocabulary Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'serif',
              fontWeight: 600,
              color: '#1e293b',
              mb: 2,
              textAlign: 'center'
            }}
          >
            Essential Chinese Vocabulary
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#64748b',
              maxWidth: 800,
              mx: 'auto',
              textAlign: 'center',
              mb: 6
            }}
          >
            Master these fundamental Chinese words and phrases organized by themes
          </Typography>

          <Grid container spacing={4}>
            {Object.entries(essentialVocabulary).map(([key, category]) => (
              <Grid item xs={12} md={6} lg={4} key={key}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: '2rem',
                        mr: 2
                      }}
                    >
                      {category.icon}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        fontSize: '1.1rem'
                      }}
                    >
                      {category.title}
                    </Typography>
                  </Box>
                  
                  <List dense sx={{ p: 0 }}>
                    {category.words.map((word, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          px: 0,
                          py: 0.5,
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: 'rgba(59, 130, 246, 0.05)'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle sx={{ fontSize: '1rem', color: '#10b981' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={word}
                          primaryTypographyProps={{
                            sx: {
                              fontSize: '0.95rem',
                              color: '#374151',
                              fontWeight: 500
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
      </Box>
    </>
  );
}

export default Knowledge;
